import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sun, House, ChatCircle, ChartBar, User, Bell } from '@phosphor-icons/react';
import api from '@/services/api';

const NAV = [
  { path: '/my-home', label: 'Home', icon: House },
  { path: '/my-today', label: 'Today', icon: Sun },
  { path: '/my-leads', label: 'My Leads', icon: ChatCircle, badgeKey: 'unread_inbox' },
  { path: '/my-stats', label: 'My Stats', icon: ChartBar },
  { path: '/me', label: 'Me', icon: User },
];

export default function SalespersonLayout() {
  const { user, logout, wsSubscribe } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [badgeCounts, setBadgeCounts] = useState({ unread_inbox: 0 });
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const notifRef = useRef(null);

  const refreshNotificationState = useCallback(async () => {
    try {
      const [cRes, lRes] = await Promise.all([
        api.get('/notifications/count'),
        api.get('/notifications'),
      ]);
      setNotificationsUnread(cRes.data?.unread || 0);
      const raw = lRes.data;
      setNotifs(Array.isArray(raw) ? raw : (raw?.notifications ?? []));
    } catch {
      setNotifs([]);
    }
  }, []);

  useEffect(() => {
    if (!wsSubscribe) return;
    const unsub = wsSubscribe('new_message', (data) => {
      setBadgeCounts((prev) => ({ ...prev, unread_inbox: (prev.unread_inbox || 0) + 1 }));
      refreshNotificationState();
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const name = data?.contact_name || 'Customer';
          const preview = data?.preview || 'sent you a message';
          new Notification(`${name}`, {
            body: preview,
            icon: '/logo192.png',
            tag: `msg-${data?.contact_id || Date.now()}`,
          });
        } catch {}
      }
    });
    return unsub;
  }, [wsSubscribe, refreshNotificationState]);

  const loadNotifications = async () => {
    await refreshNotificationState();
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!showNotifs) return;
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showNotifs]);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await api.get('/dashboard/badge-counts');
        setBadgeCounts(res.data || {});
      } catch {}
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    refreshNotificationState();
    const t = setInterval(refreshNotificationState, 30000);
    return () => clearInterval(t);
  }, [refreshNotificationState]);

  const initials = useMemo(() => user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U', [user?.name]);

  return (
    <div className="min-h-screen bg-background flex" data-testid="salesperson-layout">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-56 flex-col border-r border-border bg-card">
        <div className="min-h-[8rem] h-auto py-2 px-3 flex items-center justify-between border-b border-border gap-2">
          <div className="flex items-center min-w-0 flex-1">
            <img
              src="/logo.svg"
              alt="LotMate"
              className="min-w-0 max-w-full object-contain object-left"
              style={{ height: '158px', width: 'auto', maxHeight: '158px', maxWidth: '100%', objectFit: 'contain' }}
            />
          </div>
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              className="relative p-2 rounded-md hover:bg-muted transition-colors"
              title="Notifications"
              onClick={() => {
                setShowNotifs((v) => {
                  const next = !v;
                  if (next) loadNotifications();
                  return next;
                });
              }}
              data-testid="salesperson-notifications-btn"
            >
              <Bell size={18} />
              {notificationsUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {notificationsUnread > 9 ? '9+' : notificationsUnread}
                </span>
              )}
            </button>
            {showNotifs && (
              <div className="absolute right-0 top-10 w-80 bg-card rounded-lg shadow-xl border border-border z-50 text-left">
                <div className="p-3 border-b border-border flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">Notifications</span>
                  {notificationsUnread > 0 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post('/notifications/read-all');
                          await refreshNotificationState();
                        } catch {}
                      }}
                    >
                      Mark all read
                    </Button>
                  ) : null}
                </div>
                {notifs.length === 0 ? (
                  <div className="p-4 text-muted-foreground text-sm">No notifications</div>
                ) : (
                  <ScrollArea className="max-h-80">
                    {notifs.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className={cn(
                          'w-full text-left p-3 border-b border-border last:border-0 hover:bg-muted/50 text-sm',
                          !n.read && 'bg-primary/5',
                        )}
                        onClick={async () => {
                          try {
                            if (n.id) await api.post(`/notifications/${n.id}/read`);
                          } catch {}
                          await refreshNotificationState();
                          if (n.contact_id) navigate(`/my-leads?contact=${n.contact_id}`);
                        }}
                      >
                        <p className="font-medium text-xs line-clamp-1">{n.title || 'Alert'}</p>
                        <p className="text-muted-foreground text-xs line-clamp-2">{n.message || n.title || '—'}</p>
                      </button>
                    ))}
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1 py-2">
          <nav className="px-3 space-y-1">
            {NAV.map(({ path, label, icon: Icon, badgeKey }) => {
              const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
              const count = badgeKey ? (badgeCounts[badgeKey] || 0) : 0;
              return (
                <NavLink
                  key={path}
                  to={path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors",
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                  data-testid={`sp-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  <span className="flex-1">{label}</span>
                  {count > 0 && <Badge className="bg-red-500 h-5 px-2 text-[10px]">{count > 9 ? '9+' : count}</Badge>}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full mt-3 h-10 rounded-xl"
            onClick={() => { logout(); navigate('/login'); }}
            data-testid="sp-logout"
          >
            Sign out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Bottom tabs (mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card border-t border-border safe-area-pb" data-testid="sp-bottom-tabs">
        <div className="flex items-center justify-around h-16">
          {NAV.map(({ path, label, icon: Icon, badgeKey }) => {
            const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
            const count = badgeKey ? (badgeCounts[badgeKey] || 0) : 0;
            return (
              <NavLink key={path} to={path} className="flex flex-col items-center justify-center gap-1 flex-1 h-full relative">
                <div className="relative">
                  <Icon size={24} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                  {count > 0 && (
                    <span className="absolute -top-1 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {count > 9 ? '9+' : count}
                    </span>
                  )}
                </div>
                <span className={cn("text-[10px] font-semibold", isActive ? 'text-primary' : 'text-muted-foreground')}>{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

